import { useCallback, useEffect, useState } from 'react';
import { useBatchId } from '../../../hooks/useBatchId';
import { useTranslation } from 'react-i18next';
import {
	PROPOSER_LOAD_DASHBOARD,
	PROPOSER_LOAD_ZONES,
} from '../../../shared/Routes';
import { Button, Table, TableColumnsType } from 'antd';
import {
	PagingConfig,
	GetProposerLoadSummaryResponse,
	ProposerLoadSummary,
	Batch,
} from '../../../shared/VolunteerApplicationServiceTypes';
import {
	hasPrivilege,
	tablePaginationConfiguration,
} from '../../../shared/Utils';
import { useProposerLoad } from './useProposerLoad';
import dayjs from 'dayjs';
import useUserSession from '../../../hooks/useUserSession';
import { PrivilegeCodes } from '../../../shared/RoleConstants';

export const ProposerDashboardSummaryTable = ({
	dataSource: { proposerLoadSummary: applications, pagination },
	onPageChange,
	pageConfig,
}: {
	dataSource: GetProposerLoadSummaryResponse;
	onPageChange: (page: number, pageSize: number) => void;
	pageConfig: PagingConfig;
}) => {
	const { onBulkAssign } = useProposerLoad();
	const { loggedInUserPrivileges } = useUserSession();
	const { t } = useTranslation();
	const { navigatewithRegionId } = useBatchId();
	const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
	const [status, setStatus] = useState<string | undefined>(undefined);

	const disableBulkAssign =
		selectedBatch &&
		(status === 'INACTIVE' ||
			(status === 'ACTIVE' &&
				dayjs(selectedBatch.proposerReviewStartDate).isSameOrBefore(
					dayjs(),
					'day'
				)))
			? true
			: false;

	useEffect(() => {
		sessionStorage.setItem(
			'DISABLE_BULK_ASSIGN',
			JSON.stringify(disableBulkAssign)
		);
		const storedBatch = sessionStorage.getItem('SELECTED_BATCH');
		if (storedBatch) {
			const parsedBatch: Batch = JSON.parse(storedBatch) as Batch;
			setSelectedBatch(parsedBatch);
			setStatus(parsedBatch.status);
		}
	}, [disableBulkAssign]);

	const tablePaginationConfig = tablePaginationConfiguration(
		pageConfig,
		pagination,
		onPageChange
	);

	const handleViewClick = useCallback(
		(application: ProposerLoadSummary) => {
			sessionStorage.setItem('REGION_NAME', application?.region || '');
			navigatewithRegionId(PROPOSER_LOAD_ZONES, application?.regionId);
		},
		[navigatewithRegionId]
	);

	const handleBulkClick = useCallback(
		(application: ProposerLoadSummary) => {
			const regionId = [Number(application?.regionId)];
			onBulkAssign(regionId, [], PROPOSER_LOAD_DASHBOARD, true);
		},
		[onBulkAssign]
	);

	const showViewZonesProposerLoad = hasPrivilege(
		PrivilegeCodes.GET_ZONES_PROPOSER_LOAD_SUMMARY,
		loggedInUserPrivileges
	);

	const baseColumns: TableColumnsType<ProposerLoadSummary> = [
		{
			title: t('region'),
			dataIndex: 'region',
			key: 'region',
			render: (__, record, index) => (
				<span
					data-testid={
						record.totalCandidatesCount && record.totalCandidatesCount > 0
							? 'proposer-load-dashboard-region'
							: ''
					}
					key={index}
				>
					{record?.region || '-'}
				</span>
			),
		},
		{
			title: t('totalCandidatesCount'),
			dataIndex: 'totalCandidatesCount',
			key: 'totalCandidatesCount',
		},
		{
			title: t('maleCandidatesCount'),
			dataIndex: 'maleCandidatesCount',
			key: 'maleCandidatesCount',
		},
		{
			title: t('femaleCandidatesCount'),
			dataIndex: 'femaleCandidatesCount',
			key: 'femaleCandidatesCount',
		},
		{
			title: t('totalProposersCount'),
			dataIndex: 'totalProposersCount',
			key: 'totalProposersCount',
		},
		{
			title: t('maleProposersCount'),
			dataIndex: 'maleProposersCount',
			key: 'maleProposersCount',
		},
		{
			title: t('femaleProposersCount'),
			dataIndex: 'femaleProposersCount',
			key: 'femaleProposersCount',
		},
		{
			title: t('action'),
			key: 'action',
			fixed: 'right',
			align: 'center',
			width: 200,
			render: (__, record) => (
				<>
					<div
						data-testid={
							record.totalCandidatesCount && record.totalCandidatesCount > 0
								? 'proposer-load-dashboard-actions'
								: ''
						}
						className="flex gap-4"
					>
						{showViewZonesProposerLoad && (
							<Button
								block
								className="border border-primary-blue text-primary-blue font-bold mt-2"
								onClick={() => handleViewClick(record)}
							>
								{t('view')}
							</Button>
						)}
						<Button
							block
							className="border border-primary-blue text-primary-blue font-bold mt-2"
							onClick={() => handleBulkClick(record)}
							disabled={disableBulkAssign}
						>
							{t('bulkAssign')}
						</Button>
					</div>
				</>
			),
		},
	];

	return (
		<>
			<div className="hidden md:block px-4">
				<div className="border rounded-xl shadow-sm bg-white">
					<Table
						columns={baseColumns}
						dataSource={applications}
						pagination={tablePaginationConfig}
						scroll={{ x: 'max-content' }}
						rowClassName={() => 'border-b border-gray-300 bg-white'}
						className="w-full overflow-x-auto !border-separate !border-spacing-y-0"
					/>
				</div>
			</div>
		</>
	);
};
